import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface CancelRegistrationDialogProps {
  /** Open state — caller controls show/hide */
  open: boolean;
  /** Fired when the user dismisses the modal (X, Cancel button, overlay) */
  onClose: () => void;
  /** Fired when the user confirms cancellation. Caller runs the mutation. */
  onConfirm: () => void;
  /** Disables the confirm button (and prevents close) while the mutation is in flight */
  loading?: boolean;
  /**
   * Optional contextual line shown below the prompt — e.g. event name,
   * position. Helps avoid mis-clicks when the user has multiple registrations.
   */
  description?: string;
  /**
   * If the registration has a paid PayPal capture, pass the per-line-item
   * amounts so the user sees what's coming back (entry fee + optional
   * non-member fee) and what's retained (transaction fee) before they
   * confirm. Cash / unpaid registrations should leave this undefined to
   * fall back to the default release-spot copy.
   */
  refundEstimate?: {
    paidTotal: number;
    entryFee: number;
    /** Pass only when the user actually paid a non-member surcharge. */
    nonMemberFee?: number;
    transactionFee: number;
    /** Non-refundable partner-swap processing fee retained on team-cancels
     *  where a swap supplemental was paid. */
    partnerSwapFeeRetained?: number;
    refundable: number;
    currency?: string;
  };
}

function formatAmount(value: number, currency?: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

/**
 * Modal confirmation for cancelling a registration. Replaces the legacy
 * `window.confirm()` flow, which was visually inconsistent with the rest
 * of the app and bypassed our toast / loading affordances.
 *
 * For PayPal-paid registrations the dialog adds an itemized refund
 * breakdown so the user knows the entry fee (and any non-member fee) will
 * be refunded but the transaction fee is retained — sets expectations
 * before they confirm.
 */
export function CancelRegistrationDialog({
  open,
  onClose,
  onConfirm,
  loading = false,
  description,
  refundEstimate,
}: CancelRegistrationDialogProps) {
  const showRefundBreakdown =
    !!refundEstimate && refundEstimate.paidTotal > 0;
  const showNonMemberLine =
    !!refundEstimate &&
    refundEstimate.nonMemberFee != null &&
    refundEstimate.nonMemberFee > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !loading) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <DialogTitle>Cancel entry?</DialogTitle>
          </div>
          <DialogDescription>
            {description ??
              (showRefundBreakdown
                ? 'Your spot will be released and you can re-enter if spots are still available. Here’s what will be refunded to your original payment method:'
                : 'Your spot will be released and any pending payment will be cleared. You can re-enter if spots are still available.')}
          </DialogDescription>
        </DialogHeader>
        {showRefundBreakdown && (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Entry fee</span>
              <span className="font-medium">
                {formatAmount(refundEstimate!.entryFee, refundEstimate!.currency)}
              </span>
            </div>
            {showNonMemberLine && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Non-member fee</span>
                <span className="font-medium">
                  {formatAmount(
                    refundEstimate!.nonMemberFee!,
                    refundEstimate!.currency,
                  )}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Transaction fee{' '}
                <span className="text-xs">(non-refundable)</span>
              </span>
              <span>
                {formatAmount(
                  refundEstimate!.transactionFee,
                  refundEstimate!.currency,
                )}
              </span>
            </div>
            {refundEstimate!.partnerSwapFeeRetained != null &&
              refundEstimate!.partnerSwapFeeRetained > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Partner-swap fee{' '}
                    <span className="text-xs">(non-refundable)</span>
                  </span>
                  <span>
                    {formatAmount(
                      refundEstimate!.partnerSwapFeeRetained,
                      refundEstimate!.currency,
                    )}
                  </span>
                </div>
              )}
            <div className="border-t border-border pt-2 flex justify-between font-semibold">
              <span>Refund amount</span>
              <span>
                {formatAmount(
                  refundEstimate!.refundable,
                  refundEstimate!.currency,
                )}
              </span>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Keep Entry
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Cancelling...' : 'Cancel Entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
